import React from 'react';
import Pagination from 'rsuite/Pagination';
import { useNavigate, useOutletContext } from 'react-router-dom';
import useSWR from 'swr';
import { api, fetcher } from '../../../services/api';
import MyLoader from '../../common/loader.component';
import { usePaginationQs } from '../../../utils/usePagination';
import { ConversationStatus } from './conversation-item';
import {
  CleanConversation,
  CleanProfile,
} from '../../../export-types/cleaned-types';
import moment from 'moment';
import 'moment/locale/ru';
import {
  useConversationNewMessages,
  useConversationStatusesCount,
} from '../../../api/conversation';
import { OutletContextParams } from './conversations-screen';
import Table from 'rsuite/Table';
import classNames from 'classnames';
import { IdCell } from '../../common/cards/id-cell-card.component';
import { Manager } from '../../common/cards/manager-cell-card.component';
import { IconRefund } from '../../common/icons/IconRefund';
import { IconDefectCheck } from '../../common/icons/IconDefectCheck';
import { IconAdminAssistance } from '../../common/icons/IconAdminAssistance';
import { IconDelayed } from '../../common/icons/IconDelayed';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { Direction } from '../../../services/transaction.service';
// @ts-ignore
import { useBranch } from 'baobab-react/hooks';

type StatusProps = {
  status: ConversationStatus | null;
};

export type ConversationRow = CleanConversation & { pinned?: boolean };

const iconMap: Record<string, React.JSX.Element> = {
  refund: <IconRefund />,
  defectCheck: <IconDefectCheck />,
  adminAssistance: <IconAdminAssistance />,
  delayed: <IconDelayed />,
};

export const ConversationList: React.FC<StatusProps> = ({ status }) => {
  const navigate = useNavigate();
  const { mutate: mutateStatusesCount } = useConversationStatusesCount();
  const [direction, setDirection] = React.useState<Direction>('desc');
  const { user }: { user: CleanProfile | undefined } = useBranch({
    user: 'user',
  });

  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(25);
  const {
    debouncedInternalId,
    linked,
    start,
    end,
    statuses,
    managerId,
    withoutManager,
  } = useOutletContext<OutletContextParams>();
  const { data, error, isLoading, mutate } = useSWR<{
    total: number;
    pinned: Array<CleanConversation>;
    conversations: Array<CleanConversation>;
  }>(
    () => ({
      url: '/conversation/list',
      params: {
        status: statuses?.length ? statuses : status ? [status] : [],
        take,
        skip,
        internalId: debouncedInternalId ? debouncedInternalId : undefined,
        linked,
        start: start ? moment(start).startOf('day').toDate() : undefined,
        end: end ? moment(end).endOf('day').toDate() : undefined,
        direction,
        managerId: managerId,
        withoutManager: withoutManager,
      },
    }),
    fetcher,
  );

  const newMessageHandler = React.useCallback(
    (updatedConversation: CleanConversation) => {
      if (
        data?.conversations.some(
          (conversation) => conversation.id === updatedConversation.id,
        )
      ) {
        mutate();
      } else if (
        data?.pinned.some(
          (conversation) => conversation.id === updatedConversation.id,
        )
      ) {
        mutate();
      }
    },
    [data, mutate],
  );
  useConversationNewMessages(newMessageHandler, 'list');

  const handleClick = React.useCallback(
    (data: CleanConversation) => {
      if (data.purchaseOrder) {
        if (data.purchaseOrder.type === 'purchase') {
          navigate(`/purchases/${data.purchaseOrder.id}?chat=${data.type}`);
        } else {
          navigate(`/parcels/${data.purchaseOrder.id}?chat=${data.type}`);
        }
      } else if (data.deliveryOrder) {
        navigate(
          `/deliveries/delivery-order/${data.deliveryOrder.id}?chat=${data.type}`,
        );
      }
      if (data.customsDeliveryOrder) {
        navigate(`/customs-deliveries/order/${data.customsDeliveryOrder.id}`);
      }
    },
    [navigate],
  );

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  const total = data!.total;

  const renderMenu =
    (conversation: ConversationRow): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (
        eventKey: string | undefined,
        event: React.SyntheticEvent,
      ) => {
        event.stopPropagation();
        onClose();
        if (eventKey === '1') {
          await api.post(`/conversation/pin`, undefined, {
            params: {
              conversationId: conversation.id,
            },
          });
        } else {
          let statuses = conversation.statuses || [];
          const index = statuses.indexOf(eventKey as ConversationStatus);
          if (index !== -1) {
            if (eventKey !== 'archive') {
              const archiveIndex = statuses.indexOf('archive');
              if (archiveIndex !== -1) {
                statuses.splice(archiveIndex, 1);
              }
            }
            statuses.splice(index, 1);
          } else {
            if (eventKey === 'archive') {
              statuses = ['archive'];
            } else {
              const archiveIndex = statuses.indexOf('archive');
              if (archiveIndex !== -1) {
                statuses.splice(archiveIndex, 1);
              }
              statuses.push(eventKey as ConversationStatus);
            }
          }
          await api.post(
            `/conversation/status`,
            {
              statuses: statuses,
            },
            {
              params: {
                conversationId: conversation.id,
              },
            },
          );
        }
        mutateStatusesCount();
        mutate();
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            {!conversation.pinned ? (
              <Dropdown.Item eventKey={'1'}>
                <i className="bi bi-pin" />
                <span className="ps-2">Закрепить</span>
              </Dropdown.Item>
            ) : (
              <Dropdown.Item eventKey={'1'}>
                <i className="bi bi-pin" />
                <span className="ps-2">Открепить</span>
              </Dropdown.Item>
            )}
            <Dropdown.Item eventKey={'unread'}>
              <span className="ps-2">
                {conversation.statuses?.some((status) => status === 'unread')
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Не прочитано"
              </span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'refund'}>
              <span className="ps-2">
                {conversation.statuses?.some((status) => status === 'refund')
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Возврат"
              </span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'defectCheck'}>
              <span className="ps-2">
                {conversation.statuses?.some(
                  (status) => status === 'defectCheck',
                )
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Проверка на брак"
              </span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'adminAssistance'}>
              <span className="ps-2">
                {conversation.statuses?.some(
                  (status) => status === 'adminAssistance',
                )
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Помощь Админа"
              </span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'delayed'}>
              <span className="ps-2">
                {conversation.statuses?.some((status) => status === 'delayed')
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Задержка"
              </span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'archive'}>
              <span className="ps-2">
                {conversation.statuses?.some((status) => status === 'archive')
                  ? 'Убрать'
                  : 'Пометить'}{' '}
                "Архив"
              </span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  return (
    <>
      <div className="pb-4">
        <Table<ConversationRow, string>
          data={[
            ...data.pinned.map((conversation) => ({
              ...conversation,
              pinned: true,
            })),
            ...data.conversations,
          ]}
          headerHeight={50}
          autoHeight
          rowHeight={55}
          loading={isLoading}
          onRowClick={(rowData) => {
            handleClick(rowData);
          }}
          className={'cursor-pointer fs-14'}
        >
          <Table.Column fullText width={140}>
            <Table.HeaderCell>Номер заказа</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return rowData.purchaseOrder ? (
                  <div
                    className={classNames(
                      'text-truncate',
                      rowData.unread > 0 ? 'fw-600' : '',
                    )}
                  >
                    <IdCell
                      internalId={rowData.purchaseOrder.internalId}
                      type={rowData.purchaseOrder.type}
                    />
                  </div>
                ) : rowData.deliveryOrder ? (
                  <div
                    className={classNames(
                      'text-truncate',
                      rowData.unread > 0 ? 'fw-600' : '',
                    )}
                  >
                    <IdCell
                      internalId={rowData.deliveryOrder.internalId}
                      type="delivery"
                    />
                  </div>
                ) : (
                  rowData.customsDeliveryOrder && (
                    <div
                      className={classNames(
                        'text-truncate',
                        rowData.unread > 0 ? 'fw-600' : '',
                      )}
                    >
                      <IdCell
                        internalId={rowData.customsDeliveryOrder.internalId}
                        type="customs"
                      />
                    </div>
                  )
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={300}>
            <Table.HeaderCell>Участник</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return rowData.type === 'supplier' ? (
                  <div
                    className={classNames(
                      'text-truncate',
                      rowData.unread > 0 ? 'fw-600' : '',
                    )}
                  >
                    Поставщик{' '}
                    {rowData.deliveryOrder?.supplier?.name ??
                      rowData.purchaseOrder?.supplier?.name ??
                      ''}
                  </div>
                ) : (
                  <div
                    className={classNames(
                      'text-truncate',
                      rowData.unread > 0 ? 'fw-600' : '',
                    )}
                  >
                    Клиент{' '}
                    {rowData.deliveryOrder?.user?.internalId ??
                      rowData.purchaseOrder?.user?.internalId ??
                      ''}
                  </div>
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText flexGrow={1}>
            <Table.HeaderCell>Сообщение</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>
              className={'no-padding-cell pt-1 ps-2'}
            >
              {(rowData) => {
                return (
                  <div
                    className={classNames(
                      'text-truncate',
                      rowData.unread > 0 ? 'fw-600' : '',
                    )}
                  >
                    <div className={'fw-600'}>
                      {rowData.deliveryOrder
                        ? 'Доставка'
                        : rowData.purchaseOrder
                          ? rowData.purchaseOrder?.name
                          : rowData.customsDeliveryOrder?.name}
                    </div>
                    {rowData.lastMessage?.message}
                  </div>
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={200}>
            <Table.HeaderCell>Ответственный</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return rowData.purchaseOrder?.user?.manager ? (
                  <div className={rowData.unread > 0 ? 'fw-600' : ''}>
                    <Manager manager={rowData.purchaseOrder?.user?.manager} />
                  </div>
                ) : rowData.deliveryOrder?.user?.manager ? (
                  <div className={rowData.unread > 0 ? 'fw-600' : ''}>
                    <Manager manager={rowData.deliveryOrder?.user?.manager} />
                  </div>
                ) : (
                  <div className="col-1" />
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={190}>
            <Table.HeaderCell>
              <button
                className="d-flex gap-1 w-100 h-100 bg-transparent"
                onClick={() => {
                  setDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                }}
              >
                Дата/Время
                <i
                  className={classNames(
                    'bi',
                    direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                  )}
                ></i>
              </button>
            </Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return (
                  <div
                    className={
                      'd-flex align-items-center gap-1 pt-1 justify-content-start'
                    }
                  >
                    <div className={rowData.unread > 0 ? 'fw-600' : ''}>
                      {rowData.lastMessage
                        ? moment(rowData.lastMessage?.sentAt).format(
                            'DD.MM.YYYY HH:mm',
                          )
                        : null}
                    </div>

                    {rowData.unread > 0 && (
                      <span className="badge bg-green-500">
                        {rowData.unread}
                      </span>
                    )}
                  </div>
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={150}>
            <Table.HeaderCell>Статус</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return (
                  <div
                    className={
                      'd-flex flex-row gap-1 text-center align-items-center pt-1 justify-content-start'
                    }
                  >
                    {rowData.statuses?.map((status, index) => {
                      return iconMap[status] ? (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/conversations/' + status);
                          }}
                        >
                          {iconMap[status]}{' '}
                        </div>
                      ) : null;
                    })}
                  </div>
                );
              }}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={90}>
            <Table.HeaderCell>{''}</Table.HeaderCell>
            <Table.Cell<ConversationRow, string>>
              {(rowData) => {
                return (
                  <div className={'d-flex flex-row gap-1 align-items-center'}>
                    <i
                      onClick={async (event) => {
                        event.stopPropagation();
                        await api.post(`/conversation/pin`, undefined, {
                          params: {
                            conversationId: rowData.id,
                          },
                        });
                        mutate();
                      }}
                      className={classNames(
                        'bi bi-pin mx-2 hover-bg-light rounded-8',
                        rowData.pinned ? 'color-green' : '',
                      )}
                    />
                    {!user?.accesses.includes('salesManager') &&
                    user?.role !== 'admin' ? null : (
                      <Whisper
                        placement="leftStart"
                        trigger="click"
                        onClick={(e) => e.stopPropagation()}
                        speaker={renderMenu(rowData)}
                      >
                        <IconButton
                          appearance="subtle"
                          icon={<i className="bi bi-three-dots" />}
                        />
                      </Whisper>
                    )}
                  </div>
                );
              }}
            </Table.Cell>
          </Table.Column>
        </Table>
      </div>

      {total > take && (
        <div className="pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={total}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      )}
    </>
  );
};
